/**
 * The organization's git repositories, and the button to link one.
 *
 * Linking is how an agent gets a repository to work in: DevPulse keeps no git credential and
 * clones nothing, so "access" is an agent running in a clone of it. In a browser a repository is
 * linked by its remote; in the desktop app, by picking the folder, which also tells the app where
 * to start the local agent.
 */

import { askSensitive } from '../components/SensitiveStep'
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FolderGit2, FolderOpen, GitBranch, Link2, Radio, Unlink } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { atLeast, getSession } from '../lib/auth'
import { isDesktop, repoPick, type RepoFolder } from '../lib/desktop'
import { folderFor, forgetFolder, rememberFolder } from '../lib/repoFolders'
import { Badge, EmptyState, ErrorBanner, Skeleton } from '../components/primitives'
import { useActiveRepository } from '../lib/activeRepository'
import { Modal } from '../components/Modal'
import type { RepositoryLink } from '../lib/types'

export default function Repositories() {
  const { t } = useI18n()
  const role = getSession()?.role
  const queryClient = useQueryClient()
  const [linking, setLinking] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const { choose } = useActiveRepository()
  const { data: projects } = useQuery({ queryKey: ['repository-projects'], queryFn: api.repositories.projects })
  // Everything that reads "the repository" reloads when it changes.
  const reloadAll = () => {
    for (const key of ['repositories', 'repository-projects', 'repo-files', 'repo-branches', 'work-orders', 'prompt-queue']) {
      void queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  const move = useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string }) => api.repositories.update(id, { projectId }),
    onSuccess: reloadAll,
  })

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['repositories'],
    queryFn: api.repositories.list,
    // "Your agent is listening here" changes as agents come and go.
    refetchInterval: 15_000,
  })

  const unlink = useMutation({
    mutationFn: (repository: RepositoryLink) => api.repositories.unlink(repository.id),
    onSuccess: (result, repository) => {
      forgetFolder(repository.name)
      setNotice(result?.stoppedOrders ? t('repos.unlinkedStopped', { count: result.stoppedOrders }) : null)
      reloadAll()
    },
  })

  const linked = (data ?? []).filter((r) => r.linked)
  const seen = (data ?? []).filter((r) => !r.linked)
  const canLink = atLeast(role, 'Developer')

  return (
    <div className="stack">
      <div className="row row--between row--wrap">
        <p className="muted small" style={{ maxWidth: 720, margin: 0 }}>{t('repos.intro')}</p>
        {canLink && (
          <button type="button" className="btn btn--primary" onClick={() => setLinking(true)}>
            <Link2 size={15} /> {t('repos.link')}
          </button>
        )}
      </div>

      {error && <ErrorBanner message={t('error.generic')} onRetry={() => void refetch()} retryLabel={t('common.retry')} />}
      {notice && <div className="notice small" role="status">{notice}</div>}
      {isLoading && <Skeleton height={120} />}

      {data && linked.length === 0 && (
        <EmptyState
          title={t('repos.emptyTitle')}
          body={t('repos.emptyBody')}
          action={canLink ? <button type="button" className="btn btn--primary" onClick={() => setLinking(true)}><Link2 size={15} /> {t('repos.link')}</button> : undefined}
        />
      )}

      {linked.length > 0 && (
        <div className="repo-list">
          {linked.map((repository) => (
            <RepositoryRow
              key={repository.id}
              repository={repository}
              projects={projects ?? []}
              onMove={canLink ? (projectId) => move.mutate({ id: repository.id, projectId }) : undefined}
              onUnlink={atLeast(role, 'EngineeringManager') ? () => unlink.mutate(repository) : undefined}
            />
          ))}
        </div>
      )}

      {seen.length > 0 && (
        <section className="stack">
          <h3 className="small muted" style={{ margin: 0 }}>{t('repos.seen')}</h3>
          <div className="repo-list">
            {seen.map((repository) => (
              <RepositoryRow key={repository.id} repository={repository} onLink={canLink ? () => setLinking(true) : undefined} />
            ))}
          </div>
        </section>
      )}

      {linking && (
        <LinkDialog
          onClose={() => setLinking(false)}
          projects={projects ?? []}
          onLinked={(repository) => {
            setLinking(false)
            // The repository just linked becomes the one the app works on; what was already running
            // keeps going in its own repository.
            choose(repository.id)
            setNotice(t('repos.nowActive', { name: repository.name }))
            reloadAll()
          }}
        />
      )}
    </div>
  )
}

function RepositoryRow({
  repository,
  projects = [],
  onMove,
  onUnlink,
  onLink,
}: {
  repository: RepositoryLink
  projects?: Array<{ id: string; name: string; key: string }>
  onMove?: (projectId: string) => void
  onUnlink?: () => void
  onLink?: () => void
}) {
  const { t } = useI18n()
  const folder = isDesktop() ? folderFor(repository.name) : null

  return (
    <div className="repo-row panel">
      <FolderGit2 size={18} className="repo-row__icon" />
      <div className="stack" style={{ gap: 2, minWidth: 0, flex: 1 }}>
        <div className="row row--tight row--wrap">
          <strong className="truncate">{repository.name}</strong>
          {repository.provider && <Badge tone="neutral">{repository.provider}</Badge>}
          {repository.teamName && <Badge tone="info">{repository.teamName}</Badge>}
          {repository.linked && (repository.projectKey ? <Badge tone="accent" title={repository.projectName ?? undefined}>{repository.projectKey}</Badge> : <Badge tone="warn">{t('git.noProject')}</Badge>)}
          {repository.agentListening && (
            <Badge tone="ok">
              <Radio size={11} /> {t('repos.listening')}
            </Badge>
          )}
        </div>
        {repository.remoteUrl && <span className="tiny muted mono truncate" title={repository.remoteUrl}>{repository.remoteUrl}</span>}
        <div className="row row--tight row--wrap tiny muted">
          {repository.defaultBranch && (
            <span className="row row--tight">
              <GitBranch size={11} /> {repository.defaultBranch}
            </span>
          )}
          {folder && (
            <span className="row row--tight mono truncate" title={folder}>
              <FolderOpen size={11} /> {folder}
            </span>
          )}
        </div>
      </div>
      {onMove && projects.length > 1 && (
        <select
          className="select select--sm"
          value={repository.projectId ?? ''}
          aria-label={t('repos.moveProject')}
          title={t('repos.moveProject')}
          onChange={(event) => event.target.value && onMove(event.target.value)}
        >
          {!repository.projectId && <option value="">{t('git.noProject')}</option>}
          {projects.map((project) => (
            <option key={project.id} value={project.id}>{project.key} · {project.name}</option>
          ))}
        </select>
      )}
      {onLink && (
        <button type="button" className="btn btn--sm btn--ghost" onClick={onLink}>
          <Link2 size={13} /> {t('repos.linkShort')}
        </button>
      )}
      {onUnlink && (
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={() => {
            if (window.confirm(t('repos.unlinkConfirm', { name: repository.name }))) onUnlink()
          }}
        >
          <Unlink size={13} /> {t('repos.unlink')}
        </button>
      )}
    </div>
  )
}

/** Why the server refused a link, in the reader's language when it is one we know. */
function linkError(t: (key: string) => string, error: unknown): string {
  if (error instanceof ApiError && error.code && ['remote_has_credentials', 'invalid_remote', 'invalid_name', 'invalid_branch', 'required', 'team_required'].includes(error.code)) {
    return t(`repos.error.${error.code}`)
  }
  return t('error.generic')
}

function LinkDialog({
  onClose,
  onLinked,
  projects,
}: {
  onClose: () => void
  onLinked: (repository: RepositoryLink) => void
  projects: Array<{ id: string; name: string; key: string }>
}) {
  const { t } = useI18n()
  const desktop = isDesktop()
  const [remoteUrl, setRemoteUrl] = useState('')
  const [name, setName] = useState('')
  const [branch, setBranch] = useState('')
  const [folder, setFolder] = useState<RepoFolder | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  // Every repository belongs to a team, when the organization has teams.
  const { data: teams } = useQuery({ queryKey: ['repository-teams'], queryFn: api.repositories.teams })
  const [teamId, setTeamId] = useState('')
  // Empty: a new project named after the repository (or the existing one of that name).
  const [projectId, setProjectId] = useState('')

  const link = useMutation({
    mutationFn: () =>
      api.repositories.link({
        remoteUrl: remoteUrl.trim() || null,
        name: name.trim() || null,
        defaultBranch: branch.trim() || null,
        teamId: teamId || null,
        projectId: projectId || null,
      }),
    onSuccess: (repository) => {
      if (folder) rememberFolder(repository.name, folder.path)
      onLinked(repository)
      // A project now: what only the person decides about it (docs/refactor/18).
      askSensitive('project')
    },
    onError: (error) => setProblem(linkError(t, error)),
  })

  const pick = async () => {
    setProblem(null)
    try {
      const picked = await repoPick()
      if (!picked) return
      setFolder(picked)
      // The folder's name is the repository's name: it is what the agent's hooks report.
      setName(picked.name)
      setRemoteUrl(picked.remote_url ?? '')
      setBranch(picked.branch && picked.branch !== 'HEAD' ? picked.branch : '')
    } catch (error) {
      setProblem(String(error).includes('not-a-repository') ? t('repos.error.notRepository') : t('error.generic'))
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    if (!remoteUrl.trim() && !name.trim()) {
      setProblem(t('repos.error.required'))
      return
    }
    link.mutate()
  }

  return (
    <Modal title={t('repos.link')} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <p className="small muted" style={{ margin: 0 }}>{desktop ? t('repos.dialogDesktop') : t('repos.dialogWeb')}</p>

        {desktop && (
          <div className="stack" style={{ gap: 'var(--s1)' }}>
            <button type="button" className="btn" onClick={() => void pick()}>
              <FolderOpen size={15} /> {t('repos.pickFolder')}
            </button>
            {folder && <span className="tiny mono muted truncate" title={folder.path}>{folder.path}</span>}
          </div>
        )}

        <label className="field">
          <span className="field__label">{t('repos.remoteUrl')}</span>
          <input className="input mono" value={remoteUrl} onChange={(e) => setRemoteUrl(e.target.value)} placeholder="https://github.com/acme/shop-api.git" autoFocus={!desktop} />
          <span className="tiny muted">{t('repos.remoteHint')}</span>
        </label>

        <div className="row row--wrap">
          <label className="field" style={{ flex: 1, minWidth: 180 }}>
            <span className="field__label">{t('repos.name')}</span>
            <input className="input mono" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('repos.namePlaceholder')} />
          </label>
          <label className="field" style={{ width: 160 }}>
            <span className="field__label">{t('repos.branch')}</span>
            <input className="input mono" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" />
          </label>
        </div>

        {(teams ?? []).length > 0 && (
          <label className="field">
            <span className="field__label">{t('repos.team')}</span>
            <select className="select" value={teamId} onChange={(e) => setTeamId(e.target.value)} required>
              <option value="">{t('repos.teamPick')}</option>
              {(teams ?? []).map((team) => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
            <span className="field__hint tiny muted">{t('repos.teamGitHint')}</span>
          </label>
        )}

        <label className="field">
          <span className="field__label">{t('repos.project')}</span>
          <select className="select" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">{t('repos.projectNew', { name: name.trim() || remoteUrl.trim().split(/[/:]/).pop()?.replace(/\.git$/, '') || t('repos.projectAuto') })}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.key} · {project.name}</option>
            ))}
          </select>
          <span className="field__hint tiny muted">{t('repos.projectNewHint')}</span>
        </label>

        {problem && <ErrorBanner message={problem} />}

        <div className="row row--end">
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn--primary" disabled={link.isPending}>
            <Link2 size={15} /> {t('repos.linkShort')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
